import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Camera, Eye, Maximize2, Target, RotateCcw,
  Aperture, Focus, Move
} from 'lucide-react';
import { useI18n } from '../context/I18nContext';

// Common camera sensor sizes
const SENSOR_SIZES = {
  'full-frame': { width: 36, height: 24, name: 'Full Frame (35mm)', crop: 1.0 },
  'aps-c-canon': { width: 22.3, height: 14.9, name: 'APS-C (Canon)', crop: 1.6 },
  'aps-c': { width: 23.5, height: 15.6, name: 'APS-C (Nikon/Sony)', crop: 1.5 },
  'micro43': { width: 17.3, height: 13, name: 'Micro 4/3', crop: 2.0 },
  'medium-format': { width: 43.8, height: 32.9, name: 'Medium Format', crop: 0.79 },
  '1inch': { width: 13.2, height: 8.8, name: '1 inch', crop: 2.7 },
};

// Translation keys for the sensor size button labels (first word of the translated name is shown)
const SENSOR_NAME_KEYS = {
  'full-frame': 'fov.sensorFullFrame',
  'aps-c-canon': 'fov.sensorApsCCanon',
  'aps-c': 'fov.sensorApsC',
  'micro43': 'fov.sensorMicro43',
  'medium-format': 'fov.sensorMediumFormat',
  '1inch': 'fov.sensor1Inch',
};

// Common focal lengths
const FOCAL_PRESETS = [14, 24, 35, 50, 85, 135, 200, 400, 600];

const FOVCalculator = ({ latitude, longitude, onFOVChange }) => {
  const { t } = useI18n();
  const [focalLength, setFocalLength] = useState(50);
  const [sensorSize, setSensorSize] = useState('full-frame');
  const [cameraDirection, setCameraDirection] = useState(0);
  const [distance, setDistance] = useState(100); // meters to subject
  const [showOnMap, setShowOnMap] = useState(true);

  // Calculate FOV
  const sensor = SENSOR_SIZES[sensorSize];

  const fovHorizontal = useMemo(() => {
    return 2 * Math.atan(sensor.width / (2 * focalLength)) * 180 / Math.PI;
  }, [focalLength, sensor.width]);

  const fovVertical = useMemo(() => {
    return 2 * Math.atan(sensor.height / (2 * focalLength)) * 180 / Math.PI;
  }, [focalLength, sensor.height]);

  const fovDiagonal = useMemo(() => {
    const diagonal = Math.sqrt(sensor.width ** 2 + sensor.height ** 2);
    return 2 * Math.atan(diagonal / (2 * focalLength)) * 180 / Math.PI;
  }, [focalLength, sensor.width, sensor.height]);

  // Calculate actual coverage at distance
  const coverageWidth = useMemo(() => {
    return 2 * distance * Math.tan(fovHorizontal * Math.PI / 360);
  }, [distance, fovHorizontal]);

  const coverageHeight = useMemo(() => {
    return 2 * distance * Math.tan(fovVertical * Math.PI / 360);
  }, [distance, fovVertical]);

  // Calculate hyperfocal distance
  const circleOfConfusion = 0.03 / sensor.crop; // Adjusted for sensor size
  const hyperfocalDistance = useMemo(() => {
    const aperture = 8; // Default f/8
    return (focalLength * focalLength) / (aperture * circleOfConfusion) / 1000; // in meters
  }, [focalLength, circleOfConfusion]);

  // Equivalent focal length (35mm equivalent)
  const equivalent35mm = Math.round(focalLength * sensor.crop);

  // Notify parent of FOV change for map overlay
  useMemo(() => {
    if (onFOVChange && showOnMap) {
      onFOVChange({
        fovHorizontal,
        direction: cameraDirection,
        distance,
        enabled: showOnMap
      });
    }
  }, [fovHorizontal, cameraDirection, distance, showOnMap, onFOVChange]);

  const getLensType = (focal) => {
    if (focal < 24) return { type: t('fov.lensUltraWide'), color: 'text-purple-400' };
    if (focal < 35) return { type: t('fov.lensWide'), color: 'text-blue-400' };
    if (focal < 70) return { type: t('fov.lensNormal'), color: 'text-green-400' };
    if (focal < 135) return { type: t('fov.lensPortrait'), color: 'text-amber-400' };
    if (focal < 300) return { type: t('fov.lensTelephoto'), color: 'text-orange-400' };
    return { type: t('fov.lensSuperTele'), color: 'text-red-400' };
  };

  const lensType = getLensType(focalLength);

  return (
    <div className="space-y-4">
      {/* FOV Visualization */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Eye className="w-4 h-4 text-cyan-400" />
              {t('fov.title')}
            </CardTitle>
            <Badge className={lensType.color + ' bg-white/5'}>
              {lensType.type}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Visual FOV representation */}
          <div className="relative h-40 bg-slate-800/50 rounded-lg overflow-hidden border border-white/10">
            {/* FOV cone visualization */}
            <svg viewBox="0 0 200 120" className="w-full h-full">
              {/* Grid */}
              <defs>
                <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="200" height="120" fill="url(#grid)" />

              {/* Camera position */}
              <circle cx="100" cy="110" r="8" fill="#3b82f6" />
              <text x="100" y="115" textAnchor="middle" fill="white" fontSize="8">📷</text>

              {/* FOV cone */}
              <path
                d={`M 100 105
                    L ${100 - Math.tan(fovHorizontal * Math.PI / 360) * 90} 15
                    L ${100 + Math.tan(fovHorizontal * Math.PI / 360) * 90} 15
                    Z`}
                fill="rgba(59, 130, 246, 0.2)"
                stroke="#3b82f6"
                strokeWidth="1"
              />

              {/* Direction indicator */}
              <line
                x1="100" y1="105"
                x2="100" y2="15"
                stroke="#f59e0b"
                strokeWidth="1"
                strokeDasharray="4,4"
              />

              {/* FOV angle labels */}
              <text x="100" y="8" textAnchor="middle" fill="#f59e0b" fontSize="10" fontWeight="bold">
                {fovHorizontal.toFixed(1)}°
              </text>

              {/* Coverage width at distance */}
              <line x1="20" y1="15" x2="180" y2="15" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
              <text x="100" y="25" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="8">
                {coverageWidth.toFixed(1)}m @ {distance}m
              </text>
            </svg>
          </div>

          {/* Sensor Size Selector */}
          <div className="space-y-2">
            <Label className="text-xs text-slate-400">{t('fov.sensorSize')}</Label>
            <div className="flex flex-wrap gap-1">
              {Object.entries(SENSOR_SIZES).map(([key, value]) => (
                <Button
                  key={key}
                  variant={sensorSize === key ? 'default' : 'ghost'}
                  size="sm"
                  className={`h-7 px-2 text-xs ${sensorSize === key ? 'bg-cyan-500' : ''}`}
                  onClick={() => setSensorSize(key)}
                >
                  {t(SENSOR_NAME_KEYS[key]).split(' ')[0]}
                </Button>
              ))}
            </div>
          </div>

          {/* Focal Length */}
          <div className="space-y-2">
            <Label className="text-xs text-slate-400 flex items-center gap-2">
              <Camera className="w-3 h-3" />
              {t('fov.focalLength')}: {focalLength}mm
              <span className="text-slate-500">(≈{equivalent35mm}mm {t('fov.equiv')})</span>
            </Label>
            <Slider
              value={[focalLength]}
              onValueChange={([v]) => setFocalLength(v)}
              min={8}
              max={800}
              step={1}
            />
            <div className="flex flex-wrap gap-1">
              {FOCAL_PRESETS.map(f => (
                <Button
                  key={f}
                  variant={focalLength === f ? 'default' : 'ghost'}
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => setFocalLength(f)}
                >
                  {f}mm
                </Button>
              ))}
            </div>
          </div>

          {/* Distance to Subject */}
          <div className="space-y-2">
            <Label className="text-xs text-slate-400 flex items-center gap-2">
              <Move className="w-3 h-3" />
              {t('fov.distance')}: {distance}m
            </Label>
            <Slider
              value={[distance]}
              onValueChange={([v]) => setDistance(v)}
              min={1}
              max={1000}
              step={1}
            />
            <div className="flex flex-wrap gap-1">
              {[10, 50, 100, 250, 500, 1000].map(d => (
                <Button
                  key={d}
                  variant={distance === d ? 'default' : 'ghost'}
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => setDistance(d)}
                >
                  {d}m
                </Button>
              ))}
            </div>
          </div>

          {/* Camera Direction */}
          <div className="space-y-2">
            <Label className="text-xs text-slate-400 flex items-center gap-2">
              <Target className="w-3 h-3" />
              {t('fov.cameraDirection')}: {cameraDirection}°
            </Label>
            <Slider
              value={[cameraDirection]}
              onValueChange={([v]) => setCameraDirection(v)}
              min={0}
              max={360}
              step={5}
            />
          </div>

          {/* Map Overlay Toggle */}
          <Button
            variant={showOnMap ? 'default' : 'outline'}
            size="sm"
            className={`w-full ${showOnMap ? 'bg-cyan-500' : 'border-white/10'}`}
            onClick={() => setShowOnMap(!showOnMap)}
          >
            <Maximize2 className="w-4 h-4 mr-2" />
            {showOnMap ? t('fov.fovShownOnMap') : t('fov.showFovOnMap')}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading">{t('fov.details')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-800/50 rounded p-2 text-center">
              <p className="text-xs text-slate-500">{t('fov.horizontal')}</p>
              <p className="font-mono text-lg text-cyan-400">{fovHorizontal.toFixed(1)}°</p>
            </div>
            <div className="bg-slate-800/50 rounded p-2 text-center">
              <p className="text-xs text-slate-500">{t('fov.vertical')}</p>
              <p className="font-mono text-lg text-cyan-400">{fovVertical.toFixed(1)}°</p>
            </div>
            <div className="bg-slate-800/50 rounded p-2 text-center">
              <p className="text-xs text-slate-500">{t('fov.diagonal')}</p>
              <p className="font-mono text-lg text-cyan-400">{fovDiagonal.toFixed(1)}°</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="bg-slate-800/50 rounded p-2">
              <p className="text-xs text-slate-500">{t('fov.coverage')} @ {distance}m</p>
              <p className="font-mono text-sm text-white">
                {coverageWidth.toFixed(1)}m × {coverageHeight.toFixed(1)}m
              </p>
            </div>
            <div className="bg-slate-800/50 rounded p-2">
              <p className="text-xs text-slate-500">{t('fov.hyperfocalF8')}</p>
              <p className="font-mono text-sm text-white">
                {hyperfocalDistance.toFixed(1)}m
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Common Uses */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardContent className="p-3 text-xs text-slate-400 space-y-1">
          <p>🌅 <strong>{t('fov.useLandscapeLabel')}</strong> {t('fov.useLandscapeDesc')}</p>
          <p>👤 <strong>{t('fov.usePortraitLabel')}</strong> {t('fov.usePortraitDesc')}</p>
          <p>🦅 <strong>{t('fov.useWildlifeLabel')}</strong> {t('fov.useWildlifeDesc')}</p>
          <p>🌙 <strong>{t('fov.useAstroLabel')}</strong> {t('fov.useAstroDesc')}</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default FOVCalculator;
